/**
 * OffertKachel — one received offer as a card (P2 Placement Slice 10, Tab 4a). A document glyph (no
 * thumbnail in the repo — opens the in-app Ablage viewer), the carrier + Submissions-Nummer + Eingang,
 * the status chips, the Konfidenz, the Kernwerte strip (when normalised), the E11 / keine-Offerte /
 * nicht-zugeordnet hints, and the action row (gated on canWrite && aktionErlaubt && bearbeitbar).
 */

import { CarrierLogo } from './CarrierLogo';
import { OffertStatusBadge } from './OffertStatusBadge';
import { Button } from '../../components/ui/Button';
import { chf, dateShortDe } from '../../lib/format';
import {
  OFFER_IDP_STATUS,
  OFFER_KEINE_OFFERTE_HINWEIS,
  OFFER_MANUELL_HINWEIS,
  OFFER_NICHT_ZUGEORDNET_HINWEIS,
  OFFER_NICHT_ZUGEORDNET_LABEL,
  OFFER_THUMBNAIL_HINWEIS,
} from '@brokly/shared';
import type { OfferEintrag, OfferSubmissionGruppe } from '@brokly/shared';

export function OffertKachel({
  item,
  gruppe,
  canWrite,
  aktionErlaubt,
  onOpen,
  onZuordnen,
  onAktuell,
  onReExtract,
  onNormalisieren,
  onVerwerfen,
}: {
  item: OfferEintrag;
  gruppe: OfferSubmissionGruppe;
  canWrite: boolean;
  aktionErlaubt: boolean;
  onOpen: (item: OfferEintrag) => void;
  onZuordnen: (item: OfferEintrag) => void;
  onAktuell: (item: OfferEintrag) => void;
  onReExtract: (item: OfferEintrag) => void;
  onNormalisieren: (item: OfferEintrag) => void;
  onVerwerfen: (item: OfferEintrag) => void;
}) {
  const verworfen = item.verworfen_at !== null;
  const superseded = item.abgeloest_at !== null && !item.is_current;
  const unassigned = item.submission_id === null;
  const usable = item.idp_status !== OFFER_IDP_STATUS.KEINE_OFFERTE && !verworfen;
  const showActions = canWrite && aktionErlaubt && item.bearbeitbar;
  const conf = item.extractor_confidence;
  const n = item.normalized;

  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border border-border bg-background p-3 ${verworfen || superseded ? 'opacity-60' : ''}`}
      data-testid={`offert-kachel-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <a
          href={`/ablage/${item.document_id}`}
          target="_blank"
          rel="noreferrer"
          title={OFFER_THUMBNAIL_HINWEIS}
          onClick={() => onOpen(item)}
          className="flex h-12 w-10 shrink-0 items-center justify-center rounded border border-border bg-muted text-lg text-muted-foreground"
          data-testid={`offert-glyph-${item.id}`}
          aria-label={OFFER_THUMBNAIL_HINWEIS}
        >
          📄
        </a>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            {unassigned ? (
              <span className="text-sm font-medium text-muted-foreground">{OFFER_NICHT_ZUGEORDNET_LABEL}</span>
            ) : (
              <>
                <CarrierLogo name={gruppe.name} logoUrl={gruppe.logo_url} size="md" />
                <span className="truncate text-sm font-medium">{item.carrier_name ?? gruppe.name ?? item.carrier_uid}</span>
              </>
            )}
          </div>
          {item.submission_number && (
            <span className="font-mono text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">{item.submission_number}</span>
          )}
          <span className="truncate text-xs text-muted-foreground">{item.filename}</span>
          <span className="text-xs text-muted-foreground">{dateShortDe(item.received_at)}</span>
        </div>
      </div>

      <OffertStatusBadge
        status={item.status}
        idpStatus={item.idp_status}
        isCurrent={item.is_current}
        versionNo={item.version_no}
        verworfen={verworfen}
      />

      {conf !== null && (
        <span className="text-xs text-muted-foreground">{`Konfidenz ${Math.round(conf * 100)} %`}</span>
      )}

      {n && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid={`offert-kernwerte-${item.id}`}>
          <span>
            <span className="text-muted-foreground">Praemie/Jahr: </span>
            {n.praemie.jahr !== null ? chf(n.praemie.jahr) : '-'}
          </span>
          <span>
            <span className="text-muted-foreground">VS: </span>
            {n.versicherungssumme !== null ? chf(n.versicherungssumme) : '-'}
          </span>
          <span>
            <span className="text-muted-foreground">SB: </span>
            {n.selbstbehalt !== null ? chf(n.selbstbehalt) : '-'}
          </span>
        </div>
      )}

      {item.idp_status === OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN && (
        <div className="rounded-md border border-status-warning-border bg-status-warning-bg p-2 text-xs text-status-warning" role="note">
          {OFFER_MANUELL_HINWEIS}
        </div>
      )}
      {item.idp_status === OFFER_IDP_STATUS.KEINE_OFFERTE && (
        <p className="text-xs text-muted-foreground">{OFFER_KEINE_OFFERTE_HINWEIS}</p>
      )}
      {unassigned && !verworfen && <p className="text-xs text-muted-foreground">{OFFER_NICHT_ZUGEORDNET_HINWEIS}</p>}

      {showActions && (
        <div className="flex flex-wrap gap-2" data-testid={`offert-aktionen-${item.id}`}>
          {item.idp_status === OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN && (
            <Button variant="outline" size="sm" onClick={() => onNormalisieren(item)} data-testid={`offert-ergaenzen-${item.id}`}>
              Werte ergaenzen
            </Button>
          )}
          {(unassigned || (!item.is_current && gruppe.zuordenbar)) && (
            <Button variant="ghost" size="sm" onClick={() => onZuordnen(item)} data-testid={`offert-zuordnen-${item.id}`}>
              Zuordnen
            </Button>
          )}
          {!unassigned && !item.is_current && usable && (
            <Button variant="ghost" size="sm" onClick={() => onAktuell(item)} data-testid={`offert-aktuell-${item.id}`}>
              Als aktuelle Version setzen
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onReExtract(item)} data-testid={`offert-reextract-${item.id}`}>
            Erneut extrahieren
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onVerwerfen(item)} data-testid={`offert-verwerfen-${item.id}`}>
            Verwerfen
          </Button>
        </div>
      )}
    </div>
  );
}
